import SocialNetworksService from '@/services/social-networks/social-networks.service'
import DataLayerHelper from '@helpers/data-layer.helper'
import UserHelper from '@helpers/user.helper'
import DakenService from '@services/daken/daken.service'
import HubCoreService from '@services/hub-core/hub-core.service'
import JessicaJonesService from '@services/jessica-jones/jessica-jones.service'
import LogService from '@services/log/log.service'
import User, { Instagram, StatusProviders } from './user.interface'

export type SocialNetworksTypes = 'facebook' | 'instagram' | 'twitter' | 'youtube' | 'tiktok'

export type ModalStatusTypes =
  | 'loading'
  | 'initial'
  | 'tutorial-instagram'
  | 'username-to-connect'
  | 'instagram-info'
  | 'confirm-vinculate'
  | 'profile-category'
  | 'error'
  | 'success'

export type SocialNetworksAvailables = 'instagram' | 'facebook' | 'twitter' | 'youtube' | 'tiktok'

export interface SocialNetworksInfoParams {
  action?: string
  idProfile?: string
  idCampaign?: string
}

export interface SocialNetworkActions {
  action?: 'renew' | 'switch' | 'addAccount'
  socialHash?: string
  idProfile?: string
  socialOpen?: boolean
  socialFormOpen?: boolean
}

export interface FbPage {
  id: string
  user_id?: string
  instagram: Instagram
  instagram_business_account: Instagram
  name: string
}

export interface UserObject {
  id?: string
  JWT: string
  expiresAt?: number
  expireDate?: number
  userInfo?: User
  whitelabelLogin?: boolean
}

export type ProfileResponse = {
  userInfo?: UserObject
  users?: FbPage[]
  user?: User
  success?: boolean
  error?: ErrorHubCore
  fbPages?: FbPage[]
  err?: Error
  newUser?: User
}

export interface ErrorHubCore extends Error {
  code: string
  statusCode: number
  issues: string[]
  timestamp: string
  stack: string
  path: string
  message: string
  details: {
    message: string
    code: number
    httpStatusCode: string
  }
}

export type SocialNetworksResponse = {
  fbPages?: []
  success?: boolean
  error?: ErrorHubCore
  statusCode?: number
  message?: string
  name?: string
  bindedProfileInfo?: string
  userInfo?: User
  statusProviders?: StatusProviders
  newUser?: User
  JWT?: string
  expireDate?: number
}

export class SocialNetworks {
  type: SocialNetworksTypes = null

  logService = null
  userHelper = null
  hubCoreService = null
  jessicaService = null
  dakenService = null
  socialNetworksService = null
  dataLayerHelper = null

  constructor(type: SocialNetworksTypes = 'instagram') {
    this.type = type

    this.userHelper = new UserHelper()
    this.hubCoreService = new HubCoreService()
    this.socialNetworksService = new SocialNetworksService()
    this.dakenService = new DakenService()
    this.jessicaService = new JessicaJonesService()
    this.dataLayerHelper = new DataLayerHelper()
    this.logService = new LogService()
    this.logService.init('SocialNetworks Interface')
  }

  async addSocialNetwork(hashSocialProvider: string, id = null): Promise<SocialNetworksResponse> {
    try {
      return await this.socialNetworksService.addSocialNetwork(hashSocialProvider, this.type, id)
    } catch (err) {
      this.logService.error({ message: 'Failed to change profile', error: err })
      throw {
        success: false,
        error: err,
      }
    }
  }

  async getInstagramList(hashSocialProvider: string) {
    return this.hubCoreService
      .getInstagramList(hashSocialProvider)
      .then((response) => {
        return response.data
      })
      .catch((err) => {
        this.logService.error({ message: 'Failed to change profile', error: err })
        throw err
      })
  }

  async getSocialNetworkInfo(hashSocialProvider: string, idProfile = null, getList = true) {
    let response = null
    //  Get the user from hash
    if (this.type === 'facebook' && getList) {
      try {
        response = await this.getInstagramList(hashSocialProvider)
        const { fbPages } = response

        if (fbPages?.length > 0) {
          return {
            users: fbPages,
            success: true,
            user: null,
          }
        } else {
          throw { response: { data: { code: 'HC004', message: 'No pages found' } } }
        }
      } catch (err) {
        await this.dataLayerHelper?.sendErrorToDataLayer({ errorCode: err?.response?.data, socialNetwork: 'facebook' })
        return {
          users: [],
          success: false,
          user: null,
          error: err?.response?.data,
        }
      }
    } else {
      try {
        await this.addSocialNetwork(hashSocialProvider, idProfile)
      } catch (err) {
        return {
          success: false,
          error: err?.error,
          users: [],
          user: null,
        }
      }
    }

    const refreshToken = await this.jessicaService?.refreshToken()
    const influencerInfo = await this.dakenService?.getInfluencersInfo(refreshToken?.idToken)

    const bindedProfileInfo = influencerInfo?.identities?.find(
      (identity) => identity?.id === (idProfile ?? influencerInfo?.[this.type]?.[0]?.id)
    )

    return {
      users: [bindedProfileInfo],
      newUser: {
        userInfo: { ...influencerInfo },
        ...bindedProfileInfo,
        statusProviders: influencerInfo?.statusProviders,
      },
      success: true,
      network: this.type,
    }
  }

  async renewToken(hashSocialProvider: string, idProfile: string = null): Promise<ProfileResponse> {
    try {
      await this.socialNetworksService.renewToken(hashSocialProvider, this.type, idProfile)
      const refreshToken = await this.jessicaService?.refreshToken()
      const userInfo = await this.dakenService?.getInfluencersInfo(refreshToken?.idToken)
      if (refreshToken?.idToken && refreshToken?.expiresIn) {
        return {
          userInfo: { JWT: refreshToken?.idToken, userInfo },
          user: userInfo,
          success: true,
        }
      }

      throw new Error('Error trying to renew token')
    } catch (err) {
      this.logService.error({ message: 'Error trying to renew token', error: err })
      return {
        success: false,
        error: err,
      }
    }
  }
}
